import { useEffect, useRef, useState } from 'react';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Pencil, Plus, TrashIcon, WarningCircle } from 'assets/icons';
import Button from 'components/button';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import StyledInput from 'components/styledInput/StyledInput';
import AiSuggestedMark from './AiSuggestedMark';
import {
  MAX_ACTIVE_TASKS,
  MAX_TASK_LABEL_LENGTH,
  createFormTask,
  getTaskLabelError,
  isTaskUsed,
  moveFormTask,
  renameFormTask,
} from './qualificationTasks';
import { qualificationCountText, qualificationText } from './qualificationTexts';

const tasksText = (key, values) => qualificationText(`tasks.${key}`, values);

const labelErrorText = (errorKey) =>
  errorKey ? tasksText(errorKey, { max: MAX_TASK_LABEL_LENGTH }) : '';

const ROW_ICON_COLOR = '#374151';

const TaskConfirmation = ({ message, confirmLabel, onConfirm, onCancel }) => (
  <div
    role="alertdialog"
    aria-label={message}
    className="mt-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-700"
  >
    <p>{message}</p>
    <div className="mt-2 flex justify-end gap-2">
      <Button color="SECONDARY" size="SM" onClick={onCancel}>
        {tasksText('cancel')}
      </Button>
      <Button color="PRIMARY" size="SM" onClick={onConfirm}>
        {confirmLabel}
      </Button>
    </div>
  </div>
);

const SortableTaskRow = ({
  task,
  isEditing,
  editDraft,
  editError,
  onEditDraftChange,
  onEditKeyDown,
  onStartEdit,
  onRemove,
  confirmation,
}) => {
  const editInputRef = useRef(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: task.key,
    attributes: { roleDescription: tasksText('dragRoleDescription') },
  });

  useEffect(() => {
    if (isEditing) editInputRef.current?.focus();
  }, [isEditing]);

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={`rounded-lg border border-gray-200 bg-white py-2 pl-2 pr-1 ${
        isDragging ? 'z-10 shadow-md' : ''
      }`}
      data-testid="qualification-task-row"
    >
      <div className="flex items-center gap-2">
        <IconTooltip label={tasksText('dragTooltip')}>
          {/* A native button: dnd-kit needs a ref on the handle, which Button does not forward. */}
          <button
            type="button"
            ref={setActivatorNodeRef}
            className="flex h-8 w-4 shrink-0 cursor-grab items-center justify-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500"
            aria-label={tasksText('drag', { label: task.label })}
            {...attributes}
            {...listeners}
          >
            <span aria-hidden="true" className="h-5 w-px bg-gray-300" />
          </button>
        </IconTooltip>
        {isEditing ? (
          // Enter and Escape reach this wrapper from the field: StyledInput takes no onKeyDown.
          <div className="min-w-0 flex-1" onKeyDown={onEditKeyDown}>
            <StyledInput
              type="text"
              inputRef={editInputRef}
              label={<span className="sr-only">{tasksText('editLabel')}</span>}
              value={editDraft}
              error={editError}
              onChange={(e) => onEditDraftChange(e.target.value)}
            />
          </div>
        ) : (
          <div className="flex min-w-0 flex-1 items-center">
            <button
              type="button"
              className="truncate text-left text-sm text-gray-900"
              onClick={onStartEdit}
            >
              {task.label}
            </button>
            {task.source === 'ai' && <AiSuggestedMark className="ml-2" />}
          </div>
        )}
        {!isEditing && (
          <IconTooltip label={tasksText('editTooltip')}>
            <Button
              color="TABLE"
              size="TABLE"
              className="shrink-0"
              aria-label={tasksText('edit', { label: task.label })}
              onClick={onStartEdit}
            >
              <Pencil wh={16} color={ROW_ICON_COLOR} />
            </Button>
          </IconTooltip>
        )}
        <IconTooltip label={tasksText('removeTooltip')}>
          <Button
            color="TABLE"
            size="TABLE"
            className="shrink-0"
            aria-label={tasksText('remove', { label: task.label })}
            onClick={onRemove}
          >
            <TrashIcon wh={18} color={ROW_ICON_COLOR} />
          </Button>
        </IconTooltip>
      </div>
      {confirmation}
    </li>
  );
};

/**
 * The "Tâches" tab of the qualification slide-over. It only edits `tasks`
 * (active tasks, displayed order); nothing reaches the api before the
 * slide-over is confirmed.
 */
const QualificationTasksTab = ({ tasks, setTasks }) => {
  const [newLabel, setNewLabel] = useState('');
  const [addError, setAddError] = useState('');
  const [editingKey, setEditingKey] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [editError, setEditError] = useState('');
  // { type: 'remove' | 'rename', key, label? } awaiting the admin's answer.
  const [pending, setPending] = useState(null);

  const isFull = tasks.length >= MAX_ACTIVE_TASKS;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const labelOf = (key) =>
    tasks.find((task) => task.key === key)?.label ?? '';

  const announcements = {
    onDragStart: ({ active }) =>
      tasksText('dragStart', { label: labelOf(active.id) }),
    onDragOver: ({ active, over }) =>
      over
        ? tasksText('dragOver', {
            label: labelOf(active.id),
            over: labelOf(over.id),
          })
        : undefined,
    onDragEnd: ({ active }) =>
      tasksText('dragEnd', { label: labelOf(active.id) }),
    onDragCancel: ({ active }) =>
      tasksText('dragCancel', { label: labelOf(active.id) }),
  };

  const addTask = () => {
    if (isFull) {
      setAddError(tasksText('limitReached', { max: MAX_ACTIVE_TASKS }));
      return;
    }
    const error = getTaskLabelError(newLabel, tasks);
    if (error) {
      setAddError(labelErrorText(error));
      return;
    }
    setTasks([...tasks, createFormTask(newLabel)]);
    setNewLabel('');
    setAddError('');
  };

  const stopEditing = () => {
    setEditingKey(null);
    setEditDraft('');
    setEditError('');
  };

  const startEditing = (task) => {
    setPending(null);
    setEditingKey(task.key);
    setEditDraft(task.label);
    setEditError('');
  };

  const applyRename = (key, label) => {
    setTasks(
      tasks.map((task) => (task.key === key ? renameFormTask(task, label) : task))
    );
    stopEditing();
  };

  const commitEdit = (task) => {
    const error = getTaskLabelError(editDraft, tasks, task.key);
    if (error) {
      setEditError(labelErrorText(error));
      return;
    }
    if (renameFormTask(task, editDraft) === task) {
      stopEditing();
      return;
    }
    if (isTaskUsed(task)) {
      setPending({ type: 'rename', key: task.key, label: editDraft });
      return;
    }
    applyRename(task.key, editDraft);
  };

  const handleEditKeyDown = (task) => (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit(task);
    } else if (e.key === 'Escape') {
      // Escape cancels the edit only, not the slide-over around it.
      e.preventDefault();
      e.stopPropagation();
      setPending(null);
      stopEditing();
    }
  };

  const removeTask = (key) => {
    setTasks(tasks.filter((task) => task.key !== key));
    if (editingKey === key) stopEditing();
    setPending(null);
  };

  const requestRemove = (task) => {
    if (isTaskUsed(task)) {
      setPending({ type: 'remove', key: task.key });
      return;
    }
    removeTask(task.key);
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setTasks(
      moveFormTask(
        tasks,
        tasks.findIndex((task) => task.key === active.id),
        tasks.findIndex((task) => task.key === over.id)
      )
    );
  };

  const handleNewTaskKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    addTask();
  };

  const renderConfirmation = (task) => {
    if (!pending || pending.key !== task.key) return null;
    const count = task.usedInTemplates;
    if (pending.type === 'remove') {
      return (
        <TaskConfirmation
          message={qualificationCountText('tasks.removeUsed', count)}
          confirmLabel={tasksText('confirmRemove')}
          onConfirm={() => removeTask(task.key)}
          onCancel={() => setPending(null)}
        />
      );
    }
    return (
      <TaskConfirmation
        message={qualificationCountText('tasks.renameUsed', count)}
        confirmLabel={tasksText('confirmRename')}
        onConfirm={() => {
          setPending(null);
          applyRename(task.key, pending.label);
        }}
        onCancel={() => {
          setPending(null);
          stopEditing();
        }}
      />
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="flex items-end gap-2">
          {/* Enter adds the task; StyledInput takes no onKeyDown, the event bubbles here. */}
          <div className="min-w-0 flex-1" onKeyDown={handleNewTaskKeyDown}>
            <StyledInput
              type="text"
              label={tasksText('newTask')}
              placeholder={tasksText('newTaskPlaceholder')}
              value={newLabel}
              isDisabled={isFull}
              onChange={(e) => {
                setNewLabel(e.target.value);
                setAddError('');
              }}
            />
          </div>
          <Button
            color={isFull ? 'DISABLED' : 'SECONDARY'}
            size="SM"
            className="h-10 shrink-0"
            disabled={isFull}
            onClick={addTask}
          >
            <Plus wh={18} color={isFull ? '#9CA3AF' : ROW_ICON_COLOR} />
            {tasksText('add')}
          </Button>
        </div>
        {addError && (
          <p
            className="mt-1 flex items-center gap-1 text-xs text-red-500"
            role="alert"
          >
            <WarningCircle wh={16} color="#EF4444" />
            {addError}
          </p>
        )}
        {isFull && !addError && (
          <p className="mt-1 text-xs text-gray-500">
            {tasksText('limitReached', { max: MAX_ACTIVE_TASKS })}
          </p>
        )}
      </div>

      {tasks.length === 0 ? (
        <p className="rounded-lg border border-dashed p-4 text-center text-sm text-gray-500">
          {tasksText('empty')}
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          accessibility={{
            announcements,
            screenReaderInstructions: {
              draggable: tasksText('dragInstructions'),
            },
          }}
        >
          <SortableContext
            items={tasks.map((task) => task.key)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="flex flex-col gap-2">
              {tasks.map((task) => (
                <SortableTaskRow
                  key={task.key}
                  task={task}
                  isEditing={editingKey === task.key}
                  editDraft={editDraft}
                  editError={editError}
                  onEditDraftChange={(value) => {
                    setEditDraft(value);
                    setEditError('');
                  }}
                  onEditKeyDown={handleEditKeyDown(task)}
                  onStartEdit={() => startEditing(task)}
                  onRemove={() => requestRemove(task)}
                  confirmation={renderConfirmation(task)}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

export default QualificationTasksTab;
